import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import { createNativeDecoder, type BarcodeDecoderFactory } from './barcodeDecoder'

type CameraStatus = 'starting' | 'live' | 'denied' | 'unavailable'

/** Что просим у камеры: задняя, 1080p — меньше мыльно, больше не нужно (перенос старого клиента). */
const CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: 'environment' },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
}

/**
 * Сканер камерой поверх экрана. Возвращает код ВЫЗЫВАЮЩЕМУ (это диалог, а не
 * окно реестра: адреса не имеет и по F5 не воскресает). Закрывается кнопкой —
 * жест без кнопки-дублёра запрещён (CLAUDE.md §7). Камера гасится при закрытии.
 */
export function BarcodeScanner({
  onCode,
  onClose,
  createDecoder = createNativeDecoder,
}: {
  onCode: (code: string) => void
  onClose: () => void
  createDecoder?: BarcodeDecoderFactory
}) {
  const video = useRef<HTMLVideoElement>(null)
  // Декодер — один на открытие сканера; нет детектора или камеры — известно сразу, без запроса.
  const [decoder] = useState(() => createDecoder())
  const [status, setStatus] = useState<CameraStatus>(() =>
    typeof navigator.mediaDevices?.getUserMedia === 'function' && decoder !== null ? 'starting' : 'unavailable',
  )
  const handlers = useRef({ onCode, onClose })
  useEffect(() => {
    handlers.current = { onCode, onClose }
  }, [onCode, onClose])

  useEffect(() => {
    const media = navigator.mediaDevices
    if (typeof media?.getUserMedia !== 'function' || decoder === null) return undefined
    const element = video.current
    let stream: MediaStream | null = null
    let cancelled = false
    let delivered = false
    media
      .getUserMedia({ video: CONSTRAINTS, audio: false })
      .then((got) => {
        if (cancelled) {
          got.getTracks().forEach((track) => track.stop())
          return
        }
        stream = got
        if (element !== null) {
          element.srcObject = got
          void element.play().catch(() => undefined)
          decoder.start(element, (code) => {
            // Один код — один ответ: поток кадров повторяет тот же штрихкод.
            if (delivered) return
            delivered = true
            handlers.current.onCode(code)
          })
        }
        setStatus('live')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const name = error instanceof Error ? error.name : ''
        setStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable')
      })
    return () => {
      cancelled = true
      decoder.stop()
      stream?.getTracks().forEach((track) => track.stop())
      if (element !== null) element.srcObject = null
    }
  }, [decoder])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handlers.current.onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="onec-scanner" role="dialog" aria-modal="true" aria-label="Сканер штрихкода">
      <header className="onec-scanner__header">
        <h2>Наведите камеру на штрихкод</h2>
        <Button variant="secondary" icon={<X size={16} aria-hidden="true" />} onClick={onClose}>
          Закрыть
        </Button>
      </header>
      <video ref={video} playsInline muted aria-label="Видоискатель камеры" />
      <div className="onec-scanner__status">
        {status === 'starting' && <StatusMessage kind="loading" title="Включаем камеру…" />}
        {status === 'live' && <p>Камера включена — держите штрихкод в кадре.</p>}
        {status === 'denied' && (
          <StatusMessage
            kind="forbidden"
            title="Доступ к камере запрещён"
            text="Разрешите камеру в настройках браузера или введите код вручную."
          />
        )}
        {status === 'unavailable' && (
          <StatusMessage
            kind="unavailable"
            title="Сканер камерой здесь недоступен"
            text="Браузер не даёт камеру или не умеет читать штрихкоды. Введите код вручную или сканером-клавиатурой."
          />
        )}
      </div>
    </div>
  )
}
